import React, { FC, useEffect } from 'react'

import { NetworkStatus, useApolloClient } from '@apollo/client'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'

import { toggleUpdateForeignProfileAction } from 'actions/foreignProfile/foreignProfileAction'
import { removeNotificationAction } from 'actions/notificationAction'
import { cleanForeignProfileAction } from 'actions/profileAction'
import { LoadingInterSectionObserver } from 'components/page/ForeignProfile/LoadingInterSectionObserver'
import {
  LIMIT_COUNTRIES_FOR_PROFILE,
  LIMIT_INTEREST,
} from 'components/page/Profile/Profile.constants'
import {
  ProfileRouteParams,
  inlineNoticePath,
  indexPath,
} from 'components/paths'
import { DefaultLoader } from 'components/presentational/AnimatedWatch/AnimatedWatch'
import { Replace } from 'components/system/Replace'
import { ActiveUniNoticeInline } from 'components/uninotice/ActiveUniNoticeInline'
import { NetworkErrorUniNotice } from 'components/uninotice/NetworkErrorUniNotice'
import { UniNoticeId } from 'components/uninotice/uninotice.types'
import { profileNotFoundNoticeId } from 'components/uninotice/uniNoticeIdList'
import { mergeAllUrls } from 'functions/mergeAllUrls'
import { useAuthorized } from 'hooks/useAuthorized'
import { usePrevious } from 'hooks/usePrevious'
import { useReducersInsert } from 'hooks/useReducersInsert'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'
import { foreignProfileReducer } from 'reducers/foreignProfile/foreignProfileReducer'
import { matchReducer } from 'reducers/rating/matchReducer'
import { uniNoticeReducer } from 'reducers/uniNoticeReducer'

import { userNotExist } from './function/userNotExist'
import { useForeignRootQuery } from './gql/__generated__/ForeignRoot'
import { useHitQueryParams } from './hooks/useHitQueryParams'
import { useHitQueryRemoveParams } from './hooks/useHitQueryRemoveParams'

export const ForeignProfile: FC = () => {
  useReducersInsert({
    uniNoticeReducer: uniNoticeReducer,
    match: matchReducer,
    foreignProfile: foreignProfileReducer,
  })
  const { userId } = useParams<ProfileRouteParams>()
  const hitId = useHitQueryParams()
  const dispatch = useDispatch()
  const authorized = useAuthorized()

  const { data, error, refetch, networkStatus } = useForeignRootQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    ssr: true,
    variables: {
      userId,
      limitCountries: LIMIT_COUNTRIES_FOR_PROFILE,
      limitInterest: LIMIT_INTEREST,
      hitType: Number(hitId),
      giftsLimit: LIMIT_INTEREST,
      authorized: Boolean(authorized),
    },
  })

  useHitQueryRemoveParams()

  const client = useApolloClient()

  const { activeInline, vip, updateProfile, gqlError } =
    useShallowEqualSelector(
      ({
        uniNoticeReducer: { activeInline },
        userReducer: { vip },
        foreignProfile: { updateProfile, gqlError },
      }) => ({
        activeInline,
        vip,
        updateProfile,
        gqlError,
      })
    )

  useEffect(() => {
    return () => {
      if (activeInline[UniNoticeId.user_ignored_you]) {
        client.cache.evict({
          fieldName: `user({"userId":"${userId}"})`,
        })

        dispatch(removeNotificationAction(UniNoticeId.user_ignored_you))
      }
    }
  }, [userId, activeInline, dispatch])

  useEffect(() => {
    return () => {
      dispatch(cleanForeignProfileAction())
    }
  }, [dispatch])

  const previousVip = usePrevious(vip)

  useEffect(() => {
    // В случае если купил вип, перезапросим профиль
    // (апдейт пришел по сокету)
    if (previousVip !== vip) {
      refetch().catch(console.error)
    }
  }, [previousVip, vip, refetch])

  useEffect(() => {
    if (updateProfile) {
      dispatch(toggleUpdateForeignProfileAction(false))
      refetch().catch(console.error)
    }
  }, [dispatch, refetch, updateProfile])

  switch (networkStatus) {
    case NetworkStatus.loading:
    case NetworkStatus.setVariables:
      if (!data?.user) {
        return <DefaultLoader />
      }

    case NetworkStatus.error:
      if (data?.user) {
        // Ничего не делаем. https://redmine.mamba.ru/issues/122277
      } else if (error?.graphQLErrors?.length === 0) {
        // Не знаю причин этого условия. Сохраняю текущую логику
        return <DefaultLoader />
      } else {
        console.error('Profile network error', error)
        return <NetworkErrorUniNotice />
      }

    // no default
  }

  if (
    /** Если пользователь добавил эту анкету в ингор, нам придет ошибка из GQL с нотисом */
    activeInline[UniNoticeId.user_ignored_you] ||
    /** Либо достигли лимита на просмотр анкет */
    activeInline[UniNoticeId.hit_list_first_limit_exceeded]
  ) {
    return <ActiveUniNoticeInline />
  }

  if (userNotExist(data) && !gqlError) {
    if (authorized) {
      return (
        <Replace
          to={mergeAllUrls(inlineNoticePath, profileNotFoundNoticeId)}
          uid="ForeignProfile-userNotExist"
        />
      )
    } else {
      return <Replace to={indexPath} uid="ForeignProfile" />
    }
  }

  if (data && data.user) {
    return <LoadingInterSectionObserver data={data} />
  }

  return <ActiveUniNoticeInline />
}

export default ForeignProfile
